// =============================================================================
// EmployeeReadinessCard — "Am I right for site", answered about somebody else.
// =============================================================================
// James, 22 Aug 2026, pointing at the portal widget: "could just replicate this
// widget on the employee screen with the nav to their compliance review, to
// keep it all together — a good hub for all things employee."
//
// Same view, same shared derivation, same ring, bar and legend as the portal -
// deliberately. The figure a rigger sees about themselves at 6am and the figure
// James sees on their file before rostering them must be THE SAME NUMBER from
// the same maths, or one of them is wrong and nobody knows which.
//
// Renders nothing loud when the person is outside site-compliance scope
// (office staff, test logins) - the view returns no rows for them by design.
// =============================================================================
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { Card } from "@/components/ui";
import {
  ReadinessBar, ReadinessLegend, ReadinessRing, summariseRequirements,
  type RequirementRow,
} from "@/features/compliance/complianceShared";

export default function EmployeeReadinessCard({ employeeId }: { employeeId: string }) {
  const { data: reqs = [], isLoading } = useQuery({
    queryKey: ["employee_requirements", employeeId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("v_employee_requirements")
        .select("role_id, role_name, alt_group, is_mandatory, licence_type_id, licence_name, licence_id, expiry_date, status")
        .eq("employee_id", employeeId);
      if (error) throw error;
      return (data ?? []) as unknown as RequirementRow[];
    },
  });

  const compliance = useMemo(() => summariseRequirements(reqs), [reqs]);

  // The single most urgent thing, named - a percentage says there is a problem,
  // this says which one to deal with first. Worst status wins; missing beats
  // expired beats pending beats expiring.
  const nextUp = useMemo(() => {
    const order = ["missing", "expired", "pending", "expiring"] as const;
    for (const st of order) {
      const hit = compliance.list.find(g => g.mand && g.status === st);
      if (hit) {
        return {
          what: hit.names.join(" or "),
          detail:
            st === "missing"  ? "nothing on file" :
            st === "expired"  ? "expired"         :
            st === "pending"  ? "not verified"    : "running out soon",
        };
      }
    }
    return null;
  }, [compliance]);

  // Out of scope, or nothing loaded: stay quiet rather than shouting 0% at an
  // office admin's file. Scope is a deliberate setting, not a compliance state.
  if (!isLoading && reqs.length === 0) return null;

  const Verdict = compliance.verdict.icon;
  // Same 3px verdict rule as the portal card - the band-to-colour map lives in
  // PortalHome as BAND_RULE; duplicated here at card scope rather than exported
  // because it is presentation, not a fact about compliance.
  const RULE: Record<string, string> = {
    ready: "bg-ok", renewals: "bg-warn", gaps: "bg-warn",
    not_ready: "bg-danger", unset: "bg-line",
  };

  return (
    <Card className="overflow-hidden">
      <div className={`h-[3px] w-full ${RULE[compliance.verdict.band] ?? "bg-line"}`} />
      <div className="p-5">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="text-[10px] uppercase tracking-wider2 text-text-muted font-mono inline-flex items-center gap-1.5">
            <ShieldCheck size={12}/> Right for site
          </div>
          <Link to={`/hr/licences/${employeeId}`}
            className="inline-flex items-center gap-1.5 text-[12px] text-accent hover:underline font-medium">
            Full compliance review <ArrowRight size={13}/>
          </Link>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-5">
          <div className="flex-shrink-0 self-center sm:self-auto">
            <ReadinessRing pct={compliance.pct} colorClass={compliance.verdict.text} size={96} stroke={9} />
          </div>

          <div className="min-w-0 flex-1">
            <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded ${compliance.verdict.chip} ${compliance.verdict.text} text-[13px] font-medium`}>
              <Verdict size={14}/> {compliance.verdict.label}
            </div>

            <div className="text-[12.5px] text-text-muted mt-2">
              {compliance.met} of {compliance.required} tickets current
              {(compliance.counts.expiring ?? 0) > 0 && <> · {compliance.counts.expiring} running out soon</>}
            </div>

            {compliance.required > 0 && (
              <>
                <div className="mt-3"><ReadinessBar counts={compliance.counts} height={10} /></div>
                <div className="mt-2"><ReadinessLegend counts={compliance.counts} /></div>
              </>
            )}

            {nextUp && (
              <div className="mt-3 text-[12px] text-text">
                <span className="text-text-muted">First thing to sort: </span>
                <span className="font-medium">{nextUp.what}</span>
                <span className="text-text-muted"> — {nextUp.detail}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}
